import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchSettingsProfile, updateSettingsNotify } from '@/api/settings';
import { fetchEntitlementQuota, type QuotaSummary } from '@/api/entitlement';
import { DEFAULT_AVATAR_URL } from '@/constants/avatar';

const mockUser = {
  username: 'habby6820',
  email: 'habby6820@gmail.com',
  phone: '138****8888',
  registerDate: '2026年4月12日',
  userId: '22875',
};

type ModalType = 'username' | 'email' | 'phone' | 'password' | null;

export default function SettingsPage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [formValues, setFormValues] = useState({ username: mockUser.username, email: mockUser.email, phone: mockUser.phone });
  const [saved, setSaved] = useState<string | null>(null);
  const [notifyOrder, setNotifyOrder] = useState(true);
  const [notifyPromo, setNotifyPromo] = useState(false);
  const [notifyLogin, setNotifyLogin] = useState(true);
  const [quota, setQuota] = useState<QuotaSummary | null>(null);

  useEffect(() => {
    fetchEntitlementQuota().then((res) => {
      if (res.code === 0 && res.data) setQuota(res.data);
    });
    fetchSettingsProfile().then((res) => {
      if (res.code === 0 && res.data) {
        setFormValues({
          username: res.data.userName || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
        });
        setNotifyOrder(res.data.notifyOrder === '1');
        setNotifyPromo(res.data.notifyPromo === '1');
        setNotifyLogin(res.data.notifyLogin === '1');
      }
    });
  }, []);

  useEffect(() => {
    updateSettingsNotify({
      notifyOrder,
      notifyPromo,
      notifyLogin,
    }).catch(() => {});
  }, [notifyOrder, notifyPromo, notifyLogin]);

  const handleSave = (field: string) => {
    setActiveModal(null);
    setSaved(field);
    setTimeout(() => setSaved(null), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/account" className="hover:text-gray-700 transition-colors cursor-pointer">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">账户设置</span>
        </div>

        {quota && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
            <h2 className="text-base font-semibold text-gray-800 mb-3">积分与额度</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-400 text-xs">积分余额</div>
                <div className="font-bold text-gray-900">{quota.pointsBalance}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-gray-400 text-xs">赠送搜索剩余</div>
                <div className="font-bold text-gray-900">{quota.freeSearchRemaining}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                <div className="text-gray-400 text-xs">今日会员免费（查单+搜索合计）</div>
                <div className="font-bold text-gray-900">
                  {quota.dailyCombinedUsed} / {quota.dailyCombinedLimit} 已用
                  {quota.memberActive ? ' · 会员有效' : ''}
                </div>
              </div>
            </div>
            <Link to="/invite" className="inline-block mt-3 text-sm text-blue-600 hover:underline">邀请好友赚积分 →</Link>
          </div>
        )}

        {/* Avatar Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">头像</h2>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
              <img
                src={DEFAULT_AVATAR_URL}
                alt="avatar"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-800 mb-1">{formValues.username}</div>
              <div className="text-xs text-gray-400 mb-2">用户ID: {mockUser.userId}</div>
              <button className="text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                更换头像
              </button>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">基本信息</h2>
          <div className="space-y-0">
            {[
              { label: '用户名', value: formValues.username, field: 'username' as ModalType, icon: 'ri-user-line' },
              { label: '邮箱地址', value: formValues.email, field: 'email' as ModalType, icon: 'ri-mail-line' },
              { label: '手机号码', value: formValues.phone, field: 'phone' as ModalType, icon: 'ri-phone-line' },
            ].map((item, idx, arr) => (
              <div
                key={item.field}
                className={`flex items-center justify-between py-4 ${idx < arr.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                    <i className={`${item.icon} text-gray-500`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{item.label}</div>
                    <div className="text-sm font-medium text-gray-800 mt-0.5">{item.value}</div>
                  </div>
                </div>
                <button
                  onClick={() => setActiveModal(item.field)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saved === item.field ? (
                    <span className="text-emerald-500 text-xs">已保存</span>
                  ) : (
                    <>修改 <i className="ri-arrow-right-s-line" /></>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">账户安全</h2>
          <div className="space-y-0">
            <div className="flex items-center justify-between py-4 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                  <i className="ri-lock-password-line text-gray-500" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">登录密码</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">••••••••</div>
                </div>
              </div>
              <button
                onClick={() => setActiveModal('password')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {saved === 'password' ? (
                  <span className="text-emerald-500 text-xs">已保存</span>
                ) : (
                  <>修改 <i className="ri-arrow-right-s-line" /></>
                )}
              </button>
            </div>
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg">
                  <i className="ri-calendar-line text-gray-500" />
                </div>
                <div>
                  <div className="text-xs text-gray-400">注册时间</div>
                  <div className="text-sm font-medium text-gray-800 mt-0.5">{mockUser.registerDate}</div>
                </div>
              </div>
              <span className="text-xs text-gray-400">只读</span>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">通知设置</h2>
          <div className="space-y-0">
            {[
              { label: '订单状态通知', desc: '订单完成、支付成功等通知', value: notifyOrder, setter: setNotifyOrder },
              { label: '促销活动通知', desc: '优惠活动、折扣信息推送', value: notifyPromo, setter: setNotifyPromo },
              { label: '登录安全提醒', desc: '新设备登录时发送提醒', value: notifyLogin, setter: setNotifyLogin },
            ].map((item, idx, arr) => (
              <div
                key={item.label}
                className={`flex items-center justify-between py-4 ${idx < arr.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div>
                  <div className="text-sm font-medium text-gray-800">{item.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
                </div>
                <button
                  onClick={() => item.setter(!item.value)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${item.value ? 'bg-gray-900' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${item.value ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl border border-red-100 p-6">
          <h2 className="text-base font-semibold text-red-600 mb-4">危险操作</h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-800">注销账户</div>
              <div className="text-xs text-gray-400 mt-0.5">注销后账户数据将永久删除，无法恢复</div>
            </div>
            <button className="px-4 py-2 border border-red-200 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap">
              申请注销
            </button>
          </div>
        </div>

      </main>
      <Footer />

      {/* Edit Modal */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-900">
                {activeModal === 'username' && '修改用户名'}
                {activeModal === 'email' && '修改邮箱'}
                {activeModal === 'phone' && '修改手机号'}
                {activeModal === 'password' && '修改密码'}
              </h3>
              <button
                onClick={() => setActiveModal(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              {activeModal === 'password' ? (
                <>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">当前密码</label>
                    <input
                      type="password"
                      placeholder="请输入当前密码"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">新密码</label>
                    <input
                      type="password"
                      placeholder="请输入新密码（至少8位）"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">确认新密码</label>
                    <input
                      type="password"
                      placeholder="再次输入新密码"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {activeModal === 'username' && '新用户名'}
                    {activeModal === 'email' && '新邮箱地址'}
                    {activeModal === 'phone' && '新手机号码'}
                  </label>
                  <input
                    type={activeModal === 'email' ? 'email' : 'text'}
                    defaultValue={
                      activeModal === 'username' ? formValues.username :
                      activeModal === 'email' ? formValues.email :
                      formValues.phone
                    }
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setActiveModal(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleSave(activeModal)}
                className="flex-1 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                保存修改
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
